import { captureException } from '@sentry/react';
import { type Locale } from 'date-fns';
import {
  cs,
  de,
  el,
  enGB,
  enUS,
  es,
  fr,
  hu,
  id as idLocale,
  it,
  ja,
  ko,
  ms,
  pl,
  pt,
  sk,
  tr,
  vi,
  zhCN,
} from 'date-fns/locale';
import { z } from 'zod';

export const benLanguageCodeSchema = z.enum([
  'en',
  'en-US',
  'de',
  'fr',
  'es',
  'es-XL',
  'sk',
  'el',
  'cs',
  'it',
  'ms',
  'vi',
  'zh',
  'ja',
  'ko',
  'pl',
  'tr',
  'pt',
  'hu',
  'io',
]);

export type BenLanguageCode = z.infer<typeof benLanguageCodeSchema>;

export type BenLanguageBackendCode =
  | 'En'
  | 'De'
  | 'Fr'
  | 'Es'
  | 'EsXl'
  | 'EnUs'
  | 'Sk'
  | 'El'
  | 'Cs'
  | 'It'
  | 'Ms'
  | 'Vi'
  | 'Zh'
  | 'Ja'
  | 'Ko'
  | 'Pl'
  | 'Tr'
  | 'Pt'
  | 'Hu'
  | 'Io';

interface BenLanguage {
  code: BenLanguageCode;
  backendCode: BenLanguageBackendCode;
  label: string;
  shortLabel: string;
  locale: Locale;
}

/**
 * Array of platform languages. This is the source of truth for platform languages, please don't define platform language properties anywhere else.
 * If you need to add a new language, please follow the instructions in the README.md file.
 */
export const BEN_LANGUAGES: BenLanguage[] = [
  {
    code: 'en',
    backendCode: 'En',
    label: 'English (Great Britain)',
    shortLabel: 'En',
    locale: enGB,
  },
  {
    code: 'en-US',
    backendCode: 'EnUs',
    label: 'English (United States)',
    shortLabel: 'En-US',
    locale: enUS,
  },
  {
    code: 'de',
    backendCode: 'De',
    label: 'Deutsch',
    shortLabel: 'De',
    locale: de,
  },
  {
    code: 'fr',
    backendCode: 'Fr',
    label: 'Français',
    shortLabel: 'Fr',
    locale: fr,
  },
  {
    code: 'es',
    backendCode: 'Es',
    label: 'Español (España)',
    shortLabel: 'Es',
    locale: es,
  },
  {
    code: 'es-XL',
    backendCode: 'EsXl',
    label: 'Español (Latin America)',
    shortLabel: 'Es-XL',
    locale: es,
  },
  {
    code: 'sk',
    backendCode: 'Sk',
    label: 'Slovenčina',
    shortLabel: 'Sk',
    locale: sk,
  },
  {
    code: 'el',
    backendCode: 'El',
    label: 'ελληνικά',
    shortLabel: 'El',
    locale: el,
  },
  {
    code: 'cs',
    backendCode: 'Cs',
    label: 'Čeština',
    shortLabel: 'Cs',
    locale: cs,
  },
  {
    code: 'it',
    backendCode: 'It',
    label: 'Italiano',
    shortLabel: 'It',
    locale: it,
  },
  {
    code: 'ms',
    backendCode: 'Ms',
    label: 'Bahasa Melayu',
    shortLabel: 'Ms',
    locale: ms,
  },
  {
    code: 'vi',
    backendCode: 'Vi',
    label: 'Tiếng Việt',
    shortLabel: 'Vi',
    locale: vi,
  },
  {
    code: 'zh',
    backendCode: 'Zh',
    label: '中文 (简体)',
    shortLabel: 'Zh',
    locale: zhCN,
  },
  {
    code: 'ja',
    backendCode: 'Ja',
    label: '日本語',
    shortLabel: 'Ja',
    locale: ja,
  },
  {
    code: 'ko',
    backendCode: 'Ko',
    label: '한국어',
    shortLabel: 'Ko',
    locale: ko,
  },
  {
    code: 'pl',
    backendCode: 'Pl',
    label: 'Polski',
    shortLabel: 'Pl',
    locale: pl,
  },
  {
    code: 'tr',
    backendCode: 'Tr',
    label: 'Türkçe',
    shortLabel: 'Tr',
    locale: tr,
  },
  {
    code: 'pt',
    backendCode: 'Pt',
    label: 'Português',
    shortLabel: 'Pt',
    locale: pt,
  },
  {
    code: 'hu',
    backendCode: 'Hu',
    label: 'Magyar',
    shortLabel: 'Hu',
    locale: hu,
  },
  {
    code: 'io',
    backendCode: 'Io',
    label: 'Bahasa Indonesia',
    shortLabel: 'Io',
    locale: idLocale,
  },
];

export const getBenLanguageByCode = (code: BenLanguageCode): BenLanguage => {
  const benLanguage = BEN_LANGUAGES.find((lang) => lang.code === code);
  if (benLanguage) {
    return benLanguage;
  }

  captureException(
    new Error(
      `Language not found for code ${code} in getLanguageByCode, falling back to en`
    )
  );

  // fallback to en
  const fallbackLanguage = BEN_LANGUAGES.find((lang) => lang.code === 'en');
  if (!fallbackLanguage) {
    throw new Error('English language not found in BEN_LANGUAGES array');
  }

  return fallbackLanguage;
};

export const getBenLanguageBackendCode = (
  code: BenLanguageCode
): BenLanguageBackendCode => {
  return getBenLanguageByCode(code).backendCode;
};

export const getBenLanguageLocale = (code: BenLanguageCode): Locale => {
  return getBenLanguageByCode(code).locale;
};

export const getBenLanguageLabel = (code: BenLanguageCode): string => {
  return getBenLanguageByCode(code).label;
};

export const parseBenLanguageCode = (code: string): BenLanguageCode => {
  try {
    return benLanguageCodeSchema.parse(code);
  } catch {
    captureException(
      new Error(
        `Language not found for code ${code} in parseBenLanguageCode, falling back to en`
      )
    );
    return 'en';
  }
};
